import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import {
  Notification,
  SystemNotification,
  UnreadCountResponse,
} from "@/services/notifications/types";
import { notificationService } from "@/services";
import { useAuth } from "./AuthContext";

const DISMISSED_SYSTEM_IDS_KEY = "dismissed_system_notifications";

function loadDismissedSystemIds(): Set<string> {
  try {
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem(DISMISSED_SYSTEM_IDS_KEY) : null;
    return saved ? new Set(JSON.parse(saved)) : new Set();
  } catch {
    return new Set();
  }
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  total: number;
  isLoading: boolean;
  error: string | null;
  fetchNotifications: (limit?: number, offset?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refetch: () => Promise<void>;
  // System notifications (cached, not refetched on every navigation)
  systemNotifications: SystemNotification[];
  dismissedSystemNotificationIds: Set<string>;
  activeSystemNotifications: SystemNotification[];
  fetchSystemNotifications: () => Promise<void>;
  dismissSystemNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [systemNotifications, setSystemNotifications] = useState<SystemNotification[]>([]);
  const [dismissedSystemNotificationIds, setDismissedSystemNotificationIds] = useState<Set<string>>(loadDismissedSystemIds);
  const systemNotificationsFetchedRef = useRef(false);
  const { state } = useAuth();

  // Persist dismissed system notification ids
  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(DISMISSED_SYSTEM_IDS_KEY, JSON.stringify([...dismissedSystemNotificationIds]));
    }
  }, [dismissedSystemNotificationIds]);

  const activeSystemNotifications = useMemo(
    () => systemNotifications.filter((n) => !dismissedSystemNotificationIds.has(n.id)),
    [systemNotifications, dismissedSystemNotificationIds]
  );

  const fetchSystemNotifications = useCallback(async () => {
    if (!state.isAuthenticated) {
      setSystemNotifications([]);
      return;
    }
    try {
      const response = await notificationService.getSystemNotifications({ limit: 10 });
      if (response.success && response.data) {
        setSystemNotifications(response.data);
      }
    } catch {
      // Silent fail for system notifications
    }
  }, [state.isAuthenticated]);

  const dismissSystemNotification = useCallback((id: string) => {
    setDismissedSystemNotificationIds((prev) => new Set([...prev, id]));
  }, []);

  const fetchNotifications = useCallback(
    async (limit: number = 50, offset: number = 0) => {
      if (!state.isAuthenticated) {
        setNotifications([]);
        setTotal(0);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await notificationService.getNotifications({
          limit,
          offset,
        });

        if (response.success && response.data) {
          setNotifications(response.data.notifications);
          setTotal(response.data.total);
        } else {
          throw new Error(response.message || "Failed to fetch notifications");
        }
      } catch (err: any) {
        setError(err?.message || "Failed to load notifications");
        console.warn("Notifications fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [state.isAuthenticated]
  );

  const fetchUnreadCount = useCallback(async () => {
    if (!state.isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    try {
      const response = await notificationService.getUnreadCount();

      if (response.success && response.data) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (err: any) {
      console.warn("Unread count fetch error:", err);
    }
  }, [state.isAuthenticated]);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await notificationService.markAllAsRead();

      if (response.success) {
        // Update local state - mark all notifications as read
        setNotifications((prev) =>
          prev.map((notif) => ({ ...notif, is_read: true }))
        );
        setUnreadCount(0);
        // Optionally refetch to get updated data
        await fetchUnreadCount();
      } else {
        throw new Error(response.message || "Failed to mark all as read");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to mark all as read");
      throw err;
    }
  }, [fetchUnreadCount]);

  const markAsRead = useCallback(
    async (id: string) => {
      try {
        const response = await notificationService.markAsRead({ id });

        if (response.success) {
          // Update local state
          setNotifications((prev) =>
            prev.map((notif) =>
              notif.id === id ? { ...notif, is_read: true } : notif
            )
          );
          // Update unread count
          setUnreadCount((prev) => Math.max(0, prev - 1));
        } else {
          throw new Error(response.message || "Failed to mark as read");
        }
      } catch (err: any) {
        setError(err?.message || "Failed to mark as read");
        throw err;
      }
    },
    []
  );

  const deleteNotification = useCallback(
    async (id: string) => {
      try {
        const response = await notificationService.deleteNotification({ id });

        if (response.success) {
          // Update local state - remove deleted notification
          setNotifications((prev) => prev.filter((notif) => notif.id !== id));
          setTotal((prev) => Math.max(0, prev - 1));
          // Check if deleted notification was unread
          const deletedNotif = notifications.find((n) => n.id === id);
          if (deletedNotif && !deletedNotif.is_read) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
        } else {
          throw new Error(response.message || "Failed to delete notification");
        }
      } catch (err: any) {
        setError(err?.message || "Failed to delete notification");
        throw err;
      }
    },
    [notifications]
  );

  const clearAllNotifications = useCallback(async () => {
    try {
      // Delete all notifications one by one
      const deletePromises = notifications.map((notif) =>
        notificationService.deleteNotification({ id: notif.id })
      );
      
      await Promise.all(deletePromises);
      
      // Clear local state
      setNotifications([]);
      setTotal(0);
      setUnreadCount(0);
    } catch (err: any) {
      setError(err?.message || "Failed to clear all notifications");
      throw err;
    }
  }, [notifications]);

  const refetch = useCallback(async () => {
    await Promise.all([fetchNotifications(), fetchUnreadCount()]);
  }, [fetchNotifications, fetchUnreadCount]);

  // Fetch notifications and unread count when user is authenticated
  useEffect(() => {
    if (state.isAuthenticated && !state.isLoading) {
      fetchNotifications();
      fetchUnreadCount();
    } else if (!state.isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setTotal(0);
    }
  }, [state.isAuthenticated, state.isLoading, fetchNotifications, fetchUnreadCount]);

  // Fetch system notifications once when authenticated (cached in context, not refetched on navigation)
  useEffect(() => {
    if (state.isAuthenticated && !state.isLoading && !systemNotificationsFetchedRef.current) {
      systemNotificationsFetchedRef.current = true;
      fetchSystemNotifications();
    } else if (!state.isAuthenticated) {
      systemNotificationsFetchedRef.current = false;
      setSystemNotifications([]);
    }
  }, [state.isAuthenticated, state.isLoading, fetchSystemNotifications]);

  const value = useMemo<NotificationContextType>(
    () => ({
      notifications,
      unreadCount,
      total,
      isLoading,
      error,
      fetchNotifications,
      fetchUnreadCount,
      markAllAsRead,
      markAsRead,
      deleteNotification,
      clearAllNotifications,
      refetch,
      systemNotifications,
      dismissedSystemNotificationIds,
      activeSystemNotifications,
      fetchSystemNotifications,
      dismissSystemNotification,
    }),
    [
      notifications,
      unreadCount,
      total,
      isLoading,
      error,
      fetchNotifications,
      fetchUnreadCount,
      markAllAsRead,
      markAsRead,
      deleteNotification,
      clearAllNotifications,
      refetch,
      systemNotifications,
      dismissedSystemNotificationIds,
      activeSystemNotifications,
      fetchSystemNotifications,
      dismissSystemNotification,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

